
import React from 'react';
import { Page } from '../types';
import { DashboardIcon, TransactionsIcon, TimeIcon, SettingsIcon, LogoIcon, AnalysisIcon, CategoryIcon, ChevronLeftIcon, HelpIcon, SuggestionIcon, DuesIcon, SubscriptionIcon } from './icons';
import { CURRENT_VERSION } from './version';

interface SidebarProps {
  currentPage: Page;
  setCurrentPage: (page: Page) => void;
  isOpen: boolean;
  toggleSidebar: () => void;
  onOpenLegalModal: (type: 'privacy' | 'terms' | 'legal' | 'cookies') => void;
  onOpenVersionModal: () => void;
}

const NavItem: React.FC<{
  page: Page;
  label: string;
  // FIX: Changed icon type to allow className prop in React.cloneElement
  icon: React.ReactElement<any>;
  currentPage: Page;
  setCurrentPage: (page: Page) => void;
  isSidebarOpen: boolean;
  colorClass: string;
  id?: string;
}> = ({ page, label, icon, currentPage, setCurrentPage, isSidebarOpen, colorClass, id }) => {
  const isActive = currentPage === page;
  
  const coloredIcon = React.cloneElement(icon, {
    className: `w-6 h-6 flex-shrink-0 ${!isActive ? colorClass : ''}`,
  });
  
  return (
    <li
      id={id}
      onClick={() => setCurrentPage(page)}
      className={`flex items-center p-3 my-1 rounded-lg cursor-pointer transition-colors duration-200 ${
        isActive
          ? 'bg-primary text-white shadow-lg'
          : 'text-light-text-secondary dark:text-text-secondary hover:bg-gray-200 dark:hover:bg-surface hover:text-light-text-primary dark:hover:text-text-primary'
      }`}
      title={label}
    >
      {coloredIcon}
      <span className={`ml-4 font-medium whitespace-nowrap transition-opacity duration-200 ${isSidebarOpen ? 'opacity-100' : 'opacity-0 lg:opacity-0'}`}>{label}</span>
    </li>
  );
};

const Sidebar: React.FC<SidebarProps> = ({ currentPage, setCurrentPage, isOpen, toggleSidebar, onOpenLegalModal, onOpenVersionModal }) => {
  const iconColors: Record<Page, string> = {
    dashboard: 'text-primary',
    transactions: 'text-secondary',
    dues: 'text-accent-rose',
    subscriptions: 'text-accent-amber',
    'monthly-analysis': 'text-accent-amber',
    'time-tracking': 'text-accent-sky',
    categories: 'text-accent-rose',
    settings: 'text-accent-violet',
    help: 'text-accent-sky',
    suggestions: 'text-accent-amber',
  };

  return (
    <div className={`fixed lg:relative inset-y-0 left-0 bg-light-surface dark:bg-surface z-40 flex flex-col justify-between border-r border-light-border dark:border-border transition-transform lg:transition-width duration-300 ease-in-out ${isOpen ? 'w-64 translate-x-0' : 'w-64 lg:w-20 -translate-x-full lg:translate-x-0'}`}>
      <div>
        <div className={`flex items-center p-3 mb-6 transition-all duration-300 ${isOpen ? 'justify-between' : 'justify-center'}`}>
          <div onClick={() => setCurrentPage('dashboard')} className="flex items-center overflow-hidden cursor-pointer" title="Ir al Panel">
            <LogoIcon className="w-8 h-8 text-primary flex-shrink-0"/>
            <h1 className={`text-xl font-bold ml-3 text-light-text-primary dark:text-text-primary whitespace-nowrap transition-opacity duration-200 ${isOpen ? 'opacity-100' : 'opacity-0'}`}>Mis Cuentas 360</h1>
          </div>
        </div>
        <nav id="sidebar-nav" className="px-2">
          <ul>
            <NavItem id="nav-dashboard" page="dashboard" label="Panel" icon={<DashboardIcon />} currentPage={currentPage} setCurrentPage={setCurrentPage} isSidebarOpen={isOpen} colorClass={iconColors.dashboard} />
            <NavItem id="nav-transactions" page="transactions" label="Transacciones" icon={<TransactionsIcon />} currentPage={currentPage} setCurrentPage={setCurrentPage} isSidebarOpen={isOpen} colorClass={iconColors.transactions} />
            <NavItem id="nav-dues" page="dues" label="Vencimientos" icon={<DuesIcon />} currentPage={currentPage} setCurrentPage={setCurrentPage} isSidebarOpen={isOpen} colorClass={iconColors.dues} />
            <NavItem id="nav-subscriptions" page="subscriptions" label="Suscripciones" icon={<SubscriptionIcon />} currentPage={currentPage} setCurrentPage={setCurrentPage} isSidebarOpen={isOpen} colorClass={iconColors.subscriptions} />
            <NavItem id="nav-monthly-analysis" page="monthly-analysis" label="Análisis Mensual" icon={<AnalysisIcon />} currentPage={currentPage} setCurrentPage={setCurrentPage} isSidebarOpen={isOpen} colorClass={iconColors['monthly-analysis']} />
            <NavItem id="nav-time-tracking" page="time-tracking" label="Registro de Horas" icon={<TimeIcon />} currentPage={currentPage} setCurrentPage={setCurrentPage} isSidebarOpen={isOpen} colorClass={iconColors['time-tracking']} />
            <NavItem id="nav-categories" page="categories" label="Categorías" icon={<CategoryIcon />} currentPage={currentPage} setCurrentPage={setCurrentPage} isSidebarOpen={isOpen} colorClass={iconColors.categories} />
            <NavItem id="nav-settings" page="settings" label="Configuración" icon={<SettingsIcon />} currentPage={currentPage} setCurrentPage={setCurrentPage} isSidebarOpen={isOpen} colorClass={iconColors.settings} />
             <div className="my-2 border-t border-light-border dark:border-border mx-2"></div>
            <NavItem id="nav-help" page="help" label="Ayuda" icon={<HelpIcon />} currentPage={currentPage} setCurrentPage={setCurrentPage} isSidebarOpen={isOpen} colorClass={iconColors.help} />
            <NavItem id="nav-suggestions" page="suggestions" label="Sugerencias" icon={<SuggestionIcon />} currentPage={currentPage} setCurrentPage={setCurrentPage} isSidebarOpen={isOpen} colorClass={iconColors.suggestions} />
          </ul>
        </nav>
      </div>
       <div className="p-2 border-t border-light-border dark:border-border">
            <button 
              onClick={toggleSidebar}
              className="hidden lg:flex items-center justify-center w-full p-2 rounded-lg text-light-text-secondary dark:text-text-secondary hover:bg-light-surface dark:hover:bg-surface hover:text-light-text-primary dark:hover:text-text-primary"
              title={isOpen ? "Contraer menú" : "Expandir menú"}
            >
              <ChevronLeftIcon className={`w-6 h-6 transition-transform duration-300 ${isOpen ? '' : 'rotate-180'}`}/>
            </button>
            <div className={`px-2 text-center transition-opacity duration-200 ${isOpen ? 'opacity-100' : 'opacity-0 lg:hidden'}`}>
                <div className="text-xs text-light-text-secondary dark:text-text-secondary space-y-1 my-2">
                    <button onClick={() => onOpenLegalModal('legal')} className="hover:text-primary transition-colors">Aviso Legal</button>
                    <span> • </span>
                    <button onClick={() => onOpenLegalModal('privacy')} className="hover:text-primary transition-colors">Privacidad</button>
                    <br/>
                    <button onClick={() => onOpenLegalModal('terms')} className="hover:text-primary transition-colors">Términos de Uso</button>
                     <span> • </span>
                    <button onClick={() => onOpenLegalModal('cookies')} className="hover:text-primary transition-colors">Cookies</button>
                </div>
                <div className="border-t border-light-border dark:border-border my-3"></div>
                <div className="mb-2">
                    <button onClick={onOpenVersionModal} className="text-xs text-light-text-secondary dark:text-text-secondary hover:text-primary transition-colors">
                        Versión actual: {CURRENT_VERSION}
                    </button>
                </div>
                <div>
                  <p className="text-sm text-light-text-secondary dark:text-text-secondary">Autor: Petxus & Taldea</p>
                  <p className="text-sm text-light-text-secondary dark:text-text-secondary mt-1">© 2025 Mis Cuentas 360</p>
                </div>
            </div>
        </div>
    </div>
  );
};

export default Sidebar;